"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getThorTradeQuote = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const api_1 = require("../../../api");
const bignumber_1 = require("../../utils/bignumber");
const constants_1 = require("../../utils/constants");
const constants_2 = require("../constants");
const getThorTxData_1 = require("../utils/bitcoin/utils/getThorTxData");
const constants_3 = require("../utils/constants");
const getInboundAddressDataForChain_1 = require("../utils/getInboundAddressDataForChain");
const getTradeRate_1 = require("../utils/getTradeRate/getTradeRate");
const getUsdRate_1 = require("../utils/getUsdRate/getUsdRate");
const isRune_1 = require("../utils/isRune/isRune");
const getEvmTxFees_1 = require("../utils/txFeeHelpers/evmTxFees/getEvmTxFees");
const getUtxoTxFees_1 = require("../utils/txFeeHelpers/utxoTxFees/getUtxoTxFees");
const getSlippage_1 = require("./getSlippage");
const getThorTradeQuote = async ({ deps, input }) => {
    const { sellAsset, buyAsset, sellAmountBeforeFeesCryptoBaseUnit: sellAmountCryptoBaseUnit, bip44Params, chainId, receiveAddress, sendMax, } = input;
    if (!bip44Params) {
        throw new Error('bip44Params required in getThorTradeQuote');
    }
    try {
        const { assetReference: sellAssetReference } = (0, caip_1.fromAssetId)(sellAsset.assetId);
        const { chainId: buyAssetChainId } = (0, caip_1.fromAssetId)(buyAsset.assetId);
        const sellAdapter = deps.adapterManager.get(chainId);
        const buyAdapter = deps.adapterManager.get(buyAssetChainId);
        if (!sellAdapter || !buyAdapter)
            throw new api_1.SwapError(`[getThorTradeQuote] - No chain adapter found for ${chainId} or ${buyAssetChainId}.`, {
                code: api_1.SwapErrorTypes.UNSUPPORTED_CHAIN,
                details: { sellAssetChainId: chainId, buyAssetChainId },
            });
        const rate = await (0, getTradeRate_1.getTradeRate)(sellAsset, buyAsset.assetId, sellAmountCryptoBaseUnit, deps);
        const buyAmountCryptoBaseUnit = (0, bignumber_1.toBaseUnit)((0, bignumber_1.bnOrZero)((0, bignumber_1.fromBaseUnit)(sellAmountCryptoBaseUnit, sellAsset.precision)).times(rate), buyAsset.precision);
        const buyAssetAddressData = await (0, getInboundAddressDataForChain_1.getInboundAddressDataForChain)(deps.daemonUrl, buyAsset.assetId);
        const sellAmountThorPrecision = (0, bignumber_1.toBaseUnit)((0, bignumber_1.fromBaseUnit)(sellAmountCryptoBaseUnit, sellAsset.precision), constants_3.THORCHAIN_FIXED_PRECISION);
        const recommendedSlippage = await (0, getSlippage_1.getSlippage)({
            inputAmountThorPrecision: (0, bignumber_1.bn)(sellAmountThorPrecision),
            daemonUrl: deps.daemonUrl,
            buyAssetId: buyAsset.assetId,
            sellAssetId: sellAsset.assetId,
        });
        const estimatedBuyAssetTradeFeeFeeAssetCryptoHuman = (0, isRune_1.isRune)(buyAsset.assetId)
            ? constants_2.RUNE_OUTBOUND_TRANSACTION_FEE_CRYPTO_HUMAN.toString()
            : (0, bignumber_1.fromBaseUnit)((0, bignumber_1.bnOrZero)(buyAssetAddressData === null || buyAssetAddressData === void 0 ? void 0 : buyAssetAddressData.outbound_fee), constants_3.THORCHAIN_FIXED_PRECISION);
        const buyAssetChainFeeAssetId = buyAdapter.getFeeAssetId();
        const sellAssetUsdRate = await (0, getUsdRate_1.getUsdRate)(deps.daemonUrl, sellAsset.assetId);
        const buyFeeAssetUsdRate = await (0, getUsdRate_1.getUsdRate)(deps.daemonUrl, buyAssetChainFeeAssetId);
        const buyAssetTradeFeeUsd = (0, bignumber_1.bn)(buyFeeAssetUsdRate)
            .times(estimatedBuyAssetTradeFeeFeeAssetCryptoHuman)
            .toString();
        const minimumSellAssetAmountCryptoHuman = (0, bignumber_1.bn)(sellAssetUsdRate).isGreaterThan(0)
            ? (0, bignumber_1.bnOrZero)(buyAssetTradeFeeUsd).div(sellAssetUsdRate)
            : 0; // We don't have a valid rate for the sell asset, there is no sane minimum
        // minimum is tradeFee padded by an amount to be sure they get something back
        // usually it will be slightly more than the amount because sellAssetTradeFee is already a high estimate
        const minimumSellAssetAmountPaddedCryptoHuman = (0, bignumber_1.bnOrZero)(minimumSellAssetAmountCryptoHuman)
            .times(constants_3.THOR_MINIMUM_PADDING)
            .toString();
        const commonQuoteFields = {
            rate,
            maximum: constants_3.MAX_THORCHAIN_TRADE,
            sellAmountBeforeFeesCryptoBaseUnit: sellAmountCryptoBaseUnit,
            buyAmountCryptoBaseUnit,
            sources: [{ name: api_1.SwapperName.Thorchain, proportion: '1' }],
            buyAsset,
            sellAsset,
            bip44Params,
            minimumCryptoHuman: minimumSellAssetAmountPaddedCryptoHuman,
            recommendedSlippage: recommendedSlippage.toPrecision(),
        };
        const { chainNamespace } = (0, caip_1.fromAssetId)(sellAsset.assetId);
        switch (chainNamespace) {
            case caip_1.CHAIN_NAMESPACE.Evm:
                return (async () => {
                    const sellChainFeeAssetId = sellAdapter.getFeeAssetId();
                    const evmAddressData = await (0, getInboundAddressDataForChain_1.getInboundAddressDataForChain)(deps.daemonUrl, sellChainFeeAssetId);
                    const router = evmAddressData === null || evmAddressData === void 0 ? void 0 : evmAddressData.router;
                    if (!router)
                        throw new api_1.SwapError(`[getThorTradeQuote] No router address found for ${sellChainFeeAssetId}`);
                    const feeData = await (0, getEvmTxFees_1.getEvmTxFees)({
                        adapter: sellAdapter,
                        sellAssetReference,
                        buyAssetTradeFeeUsd,
                    });
                    return {
                        ...commonQuoteFields,
                        allowanceContract: router,
                        feeData,
                    };
                })();
            case caip_1.CHAIN_NAMESPACE.Utxo:
                return (async () => {
                    const { vault, opReturnData, pubkey } = await (0, getThorTxData_1.getThorTxInfo)({
                        deps,
                        sellAsset,
                        buyAsset,
                        sellAmountCryptoBaseUnit,
                        slippageTolerance: constants_1.DEFAULT_SLIPPAGE,
                        destinationAddress: receiveAddress,
                        xpub: input.xpub,
                        buyAssetTradeFeeUsd,
                    });
                    const feeData = await (0, getUtxoTxFees_1.getUtxoTxFees)({
                        sellAmountCryptoBaseUnit,
                        vault,
                        opReturnData,
                        pubkey,
                        sellAdapter: sellAdapter,
                        buyAssetTradeFeeUsd,
                        sendMax,
                    });
                    return {
                        ...commonQuoteFields,
                        allowanceContract: '0x0',
                        feeData,
                    };
                })();
            case caip_1.CHAIN_NAMESPACE.CosmosSdk:
                return (async () => {
                    const feeData = await sellAdapter.getFeeData({});
                    return {
                        ...commonQuoteFields,
                        allowanceContract: '0x0',
                        feeData: {
                            networkFeeCryptoBaseUnit: feeData.fast.txFee,
                            buyAssetTradeFeeUsd,
                            sellAssetTradeFeeUsd: '0',
                            chainSpecific: { estimatedGas: feeData.fast.chainSpecific.gasLimit },
                        },
                    };
                })();
            default:
                throw new api_1.SwapError('[getThorTradeQuote] - Asset chainId is not supported.', {
                    code: api_1.SwapErrorTypes.UNSUPPORTED_CHAIN,
                    details: { chainId },
                });
        }
    }
    catch (e) {
        if (e instanceof api_1.SwapError)
            throw e;
        throw new api_1.SwapError('[getThorTradeQuote]', {
            cause: e,
            code: api_1.SwapErrorTypes.TRADE_QUOTE_FAILED,
        });
    }
};
exports.getThorTradeQuote = getThorTradeQuote;
